export interface DataPoint {
  isRealCandle?: boolean;
  time: number;
  price: number;
  momentum: number;
  trend?: number;
  imf?: number;
  volume?: number;
  timestamp?: number;
  closeTime?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  sma10?: number;
  sma20?: number;
  sma50?: number;
  sma10Momentum?: number;
  sma20Momentum?: number;
  sma50Momentum?: number;
  smaMomentum?: number;
  sma10Imf?: number;
  sma20Imf?: number;
  sma50Imf?: number;
  smaImf?: number;
  tangent?: [number, number, number];
  macroTangent?: [number, number, number];
  resonance?: number;
}

export class WaveGenerator {
  private waves = [
    { amplitude: 35, frequency: 1, phase: 0 },       // Macro wave
    { amplitude: 18, frequency: 3.5, phase: 1.5 },   // Secondary Trend
    { amplitude: 10, frequency: 9, phase: 3.2 },     // Minor
    { amplitude: 6,  frequency: 24, phase: 4.1 },    // Micro
    { amplitude: 4,  frequency: 64, phase: 0.8 },    // Nano
    { amplitude: 2.5,frequency: 180, phase: 2.3 },   // Ripples
    { amplitude: 1.5,frequency: 450, phase: 5.5 },   // Fractal Noise A
    { amplitude: 0.8,frequency: 1200, phase: 1.2 },  // Fractal Noise B
  ];

  generate(timeframe: string): DataPoint[] {
    const resolutions: Record<string, number> = {
      '1y': 100,
      '1M': 150,
      '1w': 200,
      '3d': 250,
      '1d': 400,
      '12h': 3000,
      '8h': 3500,
      '6h': 4000,
      '4h': 4500,
      '2h': 5000,
      '1h': 5000,
      '30m': 5000,
      '20m': 5000,
      '15m': 5000,
      '5m': 5000,
      '3m': 5000,
      '1m': 5000,
    };
    
    const numPoints = resolutions[timeframe] || 5000;
    const maxFreq = numPoints / 4; // Nyquist limit + margin to avoid aliasing artifacts

    const points: DataPoint[] = [];

    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints; // Normalized time (0.0 to 1.0)
      
      let trend = 100 + t * 40; // Base trend + starting price
      let imf = 0;
      let momentum = 0;
      let price = trend;

      for (const w of this.waves) {
        if (w.frequency <= maxFreq) {
          const waveVal = w.amplitude * Math.sin(t * w.frequency * Math.PI * 2 + w.phase);
          const momVal = w.amplitude * Math.cos(t * w.frequency * Math.PI * 2 + w.phase);
          
          price += waveVal;
          
          if (w.frequency <= 3.5) {
             trend += waveVal; // Lowest frequencies build the trend
          } else {
             imf += waveVal; // Higher frequencies build the IMF
          }
          
          momentum += momVal;
        }
      }

      const timestamp = Date.now() - (numPoints - i) * 60000;
      points.push({ time: t, price, momentum, trend, imf, timestamp });
    }

    // Calculate SMA10 for mocked data
    for (let i = 0; i < points.length; i++) {
      const windowSize = Math.min(i + 1, 10);
      const start = Math.max(0, i - 9);
      const window = points.slice(start, i + 1);
      
      const sumPrice = window.reduce((acc, p) => acc + p.price, 0);
      const sumMom = window.reduce((acc, p) => acc + (p.momentum || 0), 0);
      const sumImf = window.reduce((acc, p) => acc + (p.imf || 0), 0);
      
      points[i].sma10 = sumPrice / windowSize;
      
      const window20 = points.slice(Math.max(0, i - 19), i + 1);
      points[i].sma20 = window20.reduce((acc, p) => acc + p.price, 0) / window20.length;
      
      const window50 = points.slice(Math.max(0, i - 49), i + 1);
      points[i].sma50 = window50.reduce((acc, p) => acc + p.price, 0) / window50.length;
      
      points[i].smaMomentum = sumMom / windowSize;
      points[i].smaImf = sumImf / windowSize;
    }

    return points;
  }
}
